import React, { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MindARThree } from 'mind-ar/dist/mindar-face-three.prod.js';
import { makeEarlobeCompute, EARLOBE_DEFAULTS } from '../../utils/earlobeAnchor';
import config from '../../config/config';
import './Earrings.css';
import { FilterAndCaptureButtons } from '../../utils/screenCapture';

const getManifestNumber = (manifest, field, fallback) => {
  const val = Number(manifest?.[field]);
  return Number.isFinite(val) ? val : fallback;
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Fallback when manifest provides no physical dimensions or category:
// 0.30 of ear-to-ear width ≈ 45mm on a 150mm face, a typical medium drop/jhumka.
const EARRING_SCALE_FACTOR = 0.30;

// Same calibration as 2D; face-relative scaling is applied in the animation loop.
const FACE_REFERENCE_MM = 150;
const SIZE_PRESETS = {
  stud:       { heightMm: 10 },
  hoopSmall:  { heightMm: 25 },
  dropSmall:  { heightMm: 32 },
  dropMedium: { heightMm: 48 },
  chandelier: { heightMm: 65 },
};
const CATEGORY_LIMITS = {
  stud:       { min: 0.05, max: 0.18 },
  hoopSmall:  { min: 0.12, max: 0.30 },
  dropSmall:  { min: 0.15, max: 0.40 },
  dropMedium: { min: 0.25, max: 0.60 },
  chandelier: { min: 0.35, max: 0.85 },
};
const DEFAULT_LIMITS = { min: 0.05, max: 0.85 };

// defaultScale is a vendor correction multiplier on top of the physical size (1.0 = no correction).
// Priority: physicalHeightMm → category preset → global default; defaultScale applied to all paths.
const resolveScaleFactor = (manifest) => {
  const ds = Number(manifest?.defaultScale);
  const multiplier = Number.isFinite(ds) && ds > 0 ? ds : 1.0;
  const heightMm = Number(manifest?.physicalHeightMm);
  if (Number.isFinite(heightMm) && heightMm > 0) return (heightMm / FACE_REFERENCE_MM) * multiplier;
  const preset = SIZE_PRESETS[manifest?.category];
  if (preset) return (preset.heightMm / FACE_REFERENCE_MM) * multiplier;
  return EARRING_SCALE_FACTOR * multiplier;
};

const resolveLimits = (manifest) => CATEGORY_LIMITS[manifest?.category] ?? DEFAULT_LIMITS;

function Combo() {
  const { sku1, sku2 } = useParams();
  const category1 = 'necklaces';
  const category2 = 'earrings';
  const containerRef = useRef(null);
  const mindarThreeRef = useRef(null);
  const sceneRef = useRef(null);
  const anchorRef = useRef({
    leftEarAnchor: null,
    rightEarAnchor: null,
    occluderAnchor: null,
    necklaceAnchor: null
  });
  const modelsRef = useRef({
    occluder: null,
    leftEarring: null,
    rightEarring: null,
    necklace: null
  });
  const rendererRef = useRef(null);
  const [selectedFilter, setSelectedFilter] = useState('none');

  // Cartoon shader
  const cartoonMaterial = useMemo(() => new THREE.ShaderMaterial({
    uniforms: {
      lightDirection: { value: new THREE.Vector3(0, 1, 1).normalize() },
      toonLevel: { value: 4.0 },
      edge: { value: 0.4 }
    },
    vertexShader: `
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform vec3 lightDirection;
      uniform float toonLevel;
      uniform float edge;
      
      varying vec3 vNormal;
      varying vec3 vPosition;
      
      void main() {
        float intensity = dot(vNormal, lightDirection);
        intensity = ceil(intensity * toonLevel) / toonLevel;
        
        // Edge detection
        float edgeIntensity = length(fwidth(vNormal));
        float isEdge = edgeIntensity > edge ? 0.0 : 1.0;
        
        vec3 toonColor = vec3(intensity * isEdge);
        gl_FragColor = vec4(toonColor, 0.5);
      }
    `,
    transparent: true,
    side: THREE.DoubleSide
  }), []);

  // Load and apply face texture
  const texture = useMemo(() => new THREE.TextureLoader().load('/assets/thumbnails/face-mask.png'), []);

  useEffect(() => {
    // console.log('Selected Filter:', selectedFilter);
    if (sceneRef.current && mindarThreeRef.current) {
      const mindarThree = mindarThreeRef.current;
      const { renderer, scene, camera } = sceneRef.current;
      
      // Remove existing face mesh if any
      const existingFaceMesh = scene.children.find(child => child.isFaceMesh);
      if (existingFaceMesh) {
        scene.remove(existingFaceMesh);
      }

      // Add new face mesh based on selected filter
      if (selectedFilter === 'none') {
        renderer.render(scene, camera);
      } else if (selectedFilter === 'cartoon') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material = cartoonMaterial;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      } else if (selectedFilter === 'animal') {
        const faceMesh = mindarThree.addFaceMesh();
        faceMesh.material.map = texture;
        faceMesh.material.transparent = true;
        faceMesh.material.needsUpdate = true;
        faceMesh.isFaceMesh = true;
        scene.add(faceMesh);
        renderer.render(scene, camera);
      }
    }
  }, [selectedFilter, cartoonMaterial, texture]);

  const fetchManifest = useCallback(async () => {
    try {
      const res = await fetch(`${config.cdnUrl}/models/3D/${category2}/${sku2}/manifest.json`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, [sku2]);

  // Initialize scene and lights
  const initializeScene = (mindarThree) => {
    const { renderer, scene, camera } = mindarThree;

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.75);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 0, 1);
    camera.add(directionalLight);
    scene.add(camera);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xbbbbff, 1);
    scene.add(hemisphereLight);

    return { renderer, scene, camera };
  };

  // Create occluder material
  const createOccluderMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      colorWrite: false,
      side: THREE.DoubleSide,
      transparent: false,
      opacity: 0,
      depthTest: true,
      depthWrite: true
    });
  }, []); // Empty dependency array since this material never needs to change

  // Load occluder model
  const loadOccluderModel = useCallback(async (material, anchor) => {
    const loader = new GLTFLoader();
    try {
      const gltf = await loader.loadAsync('/assets/models/occluder/headOccluder.glb');
      const model = gltf.scene;
      model.traverse((node) => {
        if (node.isMesh) {
          node.material = material;
        }
      });
      return model;
    } catch (error) {
      console.error('Error loading occluder model:', error);
      return null;
    }
  }, []);

  // Load necklace model
  const loadNecklaceModel = useCallback(async () => {
    const loader = new GLTFLoader();
    try {
      // Try loading from CDN first if SKU is provided
      if (sku1) {
        try {
          const cdnPath = `${config.cdnUrl}/models/${category1}/${sku1}/scene.glb`;
          const gltf = await loader.loadAsync(cdnPath);
          const model = gltf.scene;
          
          // Store initial scale and position for reference
          model.userData.baseScale = { 
            x: model.scale.x, 
            y: model.scale.y, 
            z: model.scale.z 
          };
          model.userData.basePosition = { 
            x: model.position.x, 
            y: model.position.y, 
            z: model.position.z 
          };
          
          return model;
        } catch (cdnError) {
          console.warn('Failed to load from CDN, falling back to default model:', cdnError);
        }
      }
      
      // Load default model
      const gltf = await loader.loadAsync(config.defaultNecklaceModelPath);
      const model = gltf.scene;
      
      // Store initial scale and position for reference
      model.userData.baseScale = { 
        x: model.scale.x, 
        y: model.scale.y, 
        z: model.scale.z 
      };
      model.userData.basePosition = { 
        x: model.position.x, 
        y: model.position.y, 
        z: model.position.z 
      };
      
      return model;
    } catch (error) {
      console.error('Error loading necklace model:', error);
      return null;
    }
  }, [sku1, category1]);

  // Load earrings model
  const loadEarringsModel = useCallback(async () => {
    const loader = new GLTFLoader();
    try {
      // Try loading from CDN first if SKU is provided
      if (sku2) {
        try {
          const cdnPath = `${config.cdnUrl}/models/${category2}/${sku2}/scene.glb`;
          const gltf = await loader.loadAsync(cdnPath);
          const model = gltf.scene;
          
          // Store initial scale and position for reference
          model.userData.baseScale = { 
            x: model.scale.x, 
            y: model.scale.y, 
            z: model.scale.z 
          };
          model.userData.basePosition = { 
            x: model.position.x, 
            y: model.position.y, 
            z: model.position.z 
          };
          
          return model;
        } catch (cdnError) {
          console.warn('Failed to load from CDN, falling back to default model:', cdnError);
        }
      }
      
      // Load default model
      const gltf = await loader.loadAsync(config.defaultEarringsModelPath);
      const model = gltf.scene;
      
      // Store initial scale and position for reference
      model.userData.baseScale = { 
        x: model.scale.x, 
        y: model.scale.y, 
        z: model.scale.z 
      };
      model.userData.basePosition = { 
        x: model.position.x, 
        y: model.position.y, 
        z: model.position.z 
      };
      
      return model;
    } catch (error) {
      console.error('Error loading earrings model:', error);
      return null;
    }
  }, [sku2, category2]);

  // Create necklace instance
  const createNecklace = useCallback((baseModel, rotation, position) => {
    if (!baseModel) return null;
    const model = baseModel.clone();
    
    // Apply rotation
    if (rotation) {
      if (Array.isArray(rotation)) {
        model.rotation.set(...rotation);
      } else {
        model.rotation.set(rotation.x || 0, rotation.y || 0, rotation.z || 0);
      }
    }
    
    // Apply position
    if (position) {
      if (Array.isArray(position)) {
        model.position.set(...position);
      } else {
        model.position.set(position.x || 0, position.y || 0, position.z || 0);
      }
    }
    
    return model;
  }, []);

  // Create earring instance
  const createEarring = useCallback((baseModel, rotation, position) => {
    if (!baseModel) return null;
    const model = baseModel.clone();
    
    // Apply rotation
    if (rotation) {
      if (Array.isArray(rotation)) {
        model.rotation.set(...rotation);
      } else {
        model.rotation.set(rotation.x || 0, rotation.y || 0, rotation.z || 0);
      }
    }
    
    // Apply position
    if (position) {
      if (Array.isArray(position)) {
        model.position.set(...position);
      } else {
        model.position.set(position.x || 0, position.y || 0, position.z || 0);
      }
    }
    
    return model;
  }, []);

  // Update occluder shape
  const updateOccluderShape = useCallback((occluder, morphTargetInfluences) => {
    if (!occluder) return;
    occluder.traverse((node) => {
      if (node.isMesh && node.morphTargetInfluences) {
        for (let i = 0; i < node.morphTargetInfluences.length; i++) {
          node.morphTargetInfluences[i] = morphTargetInfluences[i];
        }
      }
    });
  }, []);

  // Load models
  const loadModels = useCallback(async () => {
    if (!anchorRef.current) return;

    // Load occluder first
    if (anchorRef.current.occluderAnchor) {
      const occluder = await loadOccluderModel(createOccluderMaterial, anchorRef.current.occluderAnchor);
      if (occluder) {
        anchorRef.current.occluderAnchor.group.add(occluder);
        modelsRef.current.occluder = occluder;
      }
    }

    const necklaceBase = await loadNecklaceModel();
    const earringsBase = await loadEarringsModel();
    
    if (necklaceBase) {
      const necklace = createNecklace(necklaceBase, { x: 0, y: 0, z: 0 }, { x: 0, y: -0.2, z: 0.2 });
      if (necklace) {
        anchorRef.current.necklaceAnchor.group.add(necklace);
        modelsRef.current.necklace = necklace;
      }
    }

    if (earringsBase) {
      const leftEarring = createEarring(earringsBase, { x: 0, y: 0, z: 0 }, { x: -0.2, y: 0, z: 0 });
      const rightEarring = createEarring(earringsBase, { x: 0, y: 0, z: 0 }, { x: 0.2, y: 0, z: 0 });

      if (leftEarring && rightEarring) {
        anchorRef.current.leftEarAnchor.group.add(leftEarring);
        anchorRef.current.rightEarAnchor.group.add(rightEarring);
        modelsRef.current.leftEarring = leftEarring;
        modelsRef.current.rightEarring = rightEarring;
      }
    }
  }, [loadNecklaceModel, loadEarringsModel, createNecklace, createEarring, loadOccluderModel, createOccluderMaterial]);

  // Initialize all models and scene
  const initializeModels = useCallback(async (mindarThree) => {
    const manifest = await fetchManifest();
    const earlobeOffsets = {
      xOffset: getManifestNumber(manifest, 'earlobeXOffset', EARLOBE_DEFAULTS.xOffset),
      yOffset: getManifestNumber(manifest, 'earlobeYOffset', EARLOBE_DEFAULTS.yOffset),
      zDelta:  getManifestNumber(manifest, 'earlobeZDelta',  EARLOBE_DEFAULTS.zDelta),
    };

    anchorRef.current.leftEarAnchor  = mindarThree.addDynamicAnchor(makeEarlobeCompute('left', earlobeOffsets));
    anchorRef.current.rightEarAnchor = mindarThree.addDynamicAnchor(makeEarlobeCompute('right', earlobeOffsets));
    anchorRef.current.occluderAnchor = mindarThree.addAnchor(168);
    anchorRef.current.necklaceAnchor = mindarThree.addAnchor(152);

    await loadModels();

    // Apply manifest-derived sizing metadata now that earring models exist in modelsRef
    const scaleFactor = resolveScaleFactor(manifest);
    const limits = resolveLimits(manifest);
    const leftRotDeg  = getManifestNumber(manifest, 'leftRotationOffset', 0);
    const rightRotDeg = getManifestNumber(manifest, 'rightRotationOffset', 0);
    if (modelsRef.current.leftEarring) {
      modelsRef.current.leftEarring.rotation.z += (leftRotDeg * Math.PI) / 180;
      modelsRef.current.leftEarring.userData.scaleFactor = scaleFactor;
      modelsRef.current.leftEarring.userData.limitsMin   = limits.min;
      modelsRef.current.leftEarring.userData.limitsMax   = limits.max;
    }
    if (modelsRef.current.rightEarring) {
      modelsRef.current.rightEarring.rotation.z += (rightRotDeg * Math.PI) / 180;
      modelsRef.current.rightEarring.userData.scaleFactor = scaleFactor;
      modelsRef.current.rightEarring.userData.limitsMin   = limits.min;
      modelsRef.current.rightEarring.userData.limitsMax   = limits.max;
    }

    if (anchorRef.current.occluderAnchor) {
      anchorRef.current.occluderAnchor.onUpdate = ({ detail }) => {
        if (detail.rotation && detail.morphTargetInfluences && modelsRef.current.occluder) {
          updateOccluderShape(modelsRef.current.occluder, detail.morphTargetInfluences);
        }
      };
    }
  }, [loadModels, updateOccluderShape, fetchManifest]);

  useEffect(() => {
    let mounted = true;
    const currentMindarRef = mindarThreeRef;
    const currentSceneRef = sceneRef;
    const currentModelsRef = modelsRef.current;

    const setup = async () => {
      if (!mounted) return;

      const mindarThree = new MindARThree({
        container: containerRef.current,
        filterMinCF: 0.001,
        filterBeta: 1,
      });

      const { renderer, scene, camera } = initializeScene(mindarThree);
      rendererRef.current = renderer;
      await initializeModels(mindarThree);
      
      if (mounted) {
        currentMindarRef.current = mindarThree;
        currentSceneRef.current = { renderer, scene, camera };

        // Auto-start the AR experience
        await mindarThree.start();

        const leftPos = new THREE.Vector3();
        const rightPos = new THREE.Vector3();
        const anchorCol = new THREE.Vector3();
        let smoothedFaceWidth = 0;

        renderer.setAnimationLoop(() => {
          const { leftEarAnchor, rightEarAnchor } = anchorRef.current;
          const { leftEarring, rightEarring } = modelsRef.current;

          if (leftEarring && rightEarring && leftEarAnchor && rightEarAnchor) {
            leftEarAnchor.group.getWorldPosition(leftPos);
            rightEarAnchor.group.getWorldPosition(rightPos);
            const rawFaceWidth = leftPos.distanceTo(rightPos);

            if (rawFaceWidth > 0) {
              smoothedFaceWidth = smoothedFaceWidth === 0
                ? rawFaceWidth
                : smoothedFaceWidth * 0.85 + rawFaceWidth * 0.15;

              const sf   = leftEarring.userData.scaleFactor ?? EARRING_SCALE_FACTOR;
              const lMin = leftEarring.userData.limitsMin   ?? DEFAULT_LIMITS.min;
              const lMax = leftEarring.userData.limitsMax   ?? DEFAULT_LIMITS.max;
              // MindAR bakes faceScale (metric face width, ~14) into the anchor
              // matrix, so children render faceScale× their local scale. Divide it
              // out so the computed size lands in world units (1 unit ≈ 1 cm).
              const anchorScale = anchorCol.setFromMatrixColumn(leftEarAnchor.group.matrix, 0).length() || 1;
              const targetScale = clamp(smoothedFaceWidth * sf, smoothedFaceWidth * lMin, smoothedFaceWidth * lMax) / anchorScale;

              leftEarring.scale.setScalar(targetScale);
              rightEarring.scale.setScalar(targetScale);

              if (leftEarAnchor.group.visible) {
                const leftZ  = leftEarAnchor.group.matrix.elements[14];
                const rightZ = rightEarAnchor.group.matrix.elements[14];
                const zDiff = leftZ - rightZ;
                leftEarring.visible  = clamp(1 + zDiff / (smoothedFaceWidth * 0.6), 0, 1) > 0.03;
                rightEarring.visible = clamp(1 - zDiff / (smoothedFaceWidth * 0.6), 0, 1) > 0.03;
              }
            }
          }

          renderer.render(scene, camera);
        });
      }
    };

    setup();

    return () => {
      mounted = false;
      if (currentMindarRef.current) {
        currentMindarRef.current.stop();
        currentMindarRef.current.renderer.setAnimationLoop(null);
        currentMindarRef.current.renderer.dispose();
        currentMindarRef.current.renderer.forceContextLoss();

        if (currentSceneRef.current) {
          const { scene, renderer } = currentSceneRef.current;

          Object.values(currentModelsRef).forEach(model => {
            if (model && model.parent) {
              model.parent.remove(model);
            }
          });

          renderer.render(scene, currentSceneRef.current.camera);
          renderer.clear();
        }
      }
      containerRef.current?.replaceChildren();
    };
  }, [initializeModels]);

  // Add function to get current renderer and scene info
  const getRendererInfo = () => {
    return {
      mindarThree: mindarThreeRef.current,
      models: modelsRef.current
    };
  };

  return (
    <div className="container">
      <div className="ar-container" ref={containerRef}>
        <video className="video"></video>
      </div>
      <div className="control-panel">
        <FilterAndCaptureButtons 
          selectedFilter={selectedFilter}
          setSelectedFilter={setSelectedFilter}
          getRendererInfo={getRendererInfo}
        />
      </div>
    </div>
  );
}

export default Combo;